import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Cookie from "universal-cookie";
import config from "../config.js";
// eslint-disable-next-line import/no-webpack-loader-syntax
import Worker from "worker-loader!./worker.js";

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const subscribers = useRef({});
  const workerRef = useRef(null);
  const cookies = new Cookie();
  const userToken = cookies.get("userToken");
  const timeoutId = useRef(null);

  useEffect(() => {
    if (!userToken) {
      setError("Session expired. Please log in again.");
      return;
    }

    const worker = new Worker();
    workerRef.current = worker;

    const protocol = window.location.protocol === "https:" ? "wss" : "ws";

    // Production / Deployment
    worker.postMessage({
      type: "init",
      url: `${protocol}://${window.location.host}/ws?token=${userToken}`,
    });

    // Alternative for local development
    // worker.postMessage({
    //   type: "init",
    //   url: `ws://localhost:${config.BACKEND_PORT}?token=${userToken}`,
    // });

    worker.onmessage = (event) => {
      const { type, data } = event.data;
      if (type === "update") {
        const updates = data;
        // setData((prev) => ({ ...prev, ...updates }));

        setData((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const key in updates) {
            if (prev[key] !== updates[key]) {
              next[key] = updates[key];
              changed = true;
            }
          }
          return changed ? next : prev;
        });

        // Process subscriber callbacks in chunks
        const sources = Object.keys(updates);
        let index = 0;

        const processChunk = () => {
          const start = performance.now();
          while (index < sources.length && performance.now() - start < 8) {
            const source = sources[index];
            const value = updates[source];
            if (subscribers.current[source]) {
              subscribers.current[source].forEach((callback) =>
                callback(value)
              );
            }
            index++;
          }
          if (index < sources.length) {
            requestAnimationFrame(processChunk);
          }
        };

        requestAnimationFrame(processChunk);
      } else if (type === "error") {
        setError(event.data.message);
      } else if (type === "close") {
        setError("Data connection lost. Reconnecting...");
      }
    };

    return () => {
      worker.terminate();
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [userToken]);

  useEffect(() => {
    if (error) {
      timeoutId.current = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timeoutId.current);
    }
  }, [error]);

  const subscribe = useCallback(
    (source, callback) => {
      if (!source || typeof source !== "string" || !source.trim()) {
        setError("Invalid data source provided.");
        return () => {};
      }
      if (typeof callback !== "function") {
        setError("Invalid subscription callback.");
        return () => {};
      }

      source = source.trim();
      if (!subscribers.current[source]) {
        subscribers.current[source] = [];
        workerRef.current?.postMessage({ type: "subscribe", source });
      }
      // subscribers.current[source].push(callback);

      if (!subscribers.current[source].includes(callback)) {
        subscribers.current[source].push(callback);
      }

      if (data[source] !== undefined) {
        callback(data[source]);
      }

      return () => {
        if (subscribers.current[source]) {
          subscribers.current[source] = subscribers.current[source].filter(
            (cb) => cb !== callback
          );
          if (subscribers.current[source].length === 0) {
            delete subscribers.current[source];
            workerRef.current?.postMessage({ type: "unsubscribe", source });
          }
        }
      };
    },
    [data]
  );

  //   const subscribe = useCallback((source, callback, options = {}) => {
  //   if (!source || typeof source !== "string" || !source.trim()) {
  //     setError("Invalid data source provided.");
  //     return () => {};
  //   }
  //   if (typeof callback !== "function") {
  //     setError("Invalid subscription callback.");
  //     return () => {};
  //   }

  //   source = source.trim();
  //   const datasource = options.datasource; // we’ll pass datasource metadata here

  //   // --- Handle HTTP datasources ---
  //   if (datasource?.type === "HTTP" && datasource?.URL) {
  //     let isActive = true;

  //     const fetchData = async () => {
  //       try {
  //         const response = await fetch(datasource.URL, {
  //           method: datasource.method || "GET",
  //           headers: datasource.headers || {},
  //         });
  //         const result = await response.json();
  //         if (isActive) {
  //           callback(result);
  //         }
  //       } catch (err) {
  //         setError(`HTTP Fetch failed for ${source}: ${err.message}`);
  //       }
  //     };

  //     // Fetch once immediately
  //     fetchData();

  //     // Optional: Poll every 10 seconds
  //     const interval = setInterval(fetchData, datasource.pollInterval || 10000);

  //     return () => {
  //       isActive = false;
  //       clearInterval(interval);
  //     };
  //   }

  //   // --- Handle WebSocket (default) sources ---
  //   if (!subscribers.current[source]) {
  //     subscribers.current[source] = [];
  //     workerRef.current?.postMessage({ type: "subscribe", source });
  //   }

  //   if (!subscribers.current[source].includes(callback)) {
  //     subscribers.current[source].push(callback);
  //   }

  //   if (data[source] !== undefined) {
  //     callback(data[source]);
  //   }

  //   return () => {
  //     if (subscribers.current[source]) {
  //       subscribers.current[source] = subscribers.current[source].filter((cb) => cb !== callback);
  //       if (subscribers.current[source].length === 0) {
  //         delete subscribers.current[source];
  //         workerRef.current?.postMessage({ type: "unsubscribe", source });
  //       }
  //     }
  //   };
  // }, [data]);

  return (
    <DataContext.Provider value={{ data, subscribe }}>
      {children}
      {error && (
        <div
          style={{
            position: "fixed",
            top: "10px",
            right: "10px",
            backgroundColor: "#ef4444",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            zIndex: 1000000,
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}
    </DataContext.Provider>
  );
};

export const useSourceValue = (source) => {
  const { data, subscribe } = useContext(DataContext);
  const [value, setValue] = useState(
    source && data[source] !== undefined ? data[source] : null
  );

  useEffect(() => {
    if (!source) return;

    const handleUpdate = (newValue) => {
      setValue(newValue);
    };

    const unsubscribe = subscribe(source, handleUpdate);

    return () => {
      unsubscribe();
    };
  }, [source, subscribe]);

  return value;
};

export default DataProvider;
